import React, { useMemo, useState } from 'react'
import { Button, Grid2, } from '@mui/material'
import CustomDivider from '../../shared/utils/CustomDivider'
import { Formik, Field, Form } from "formik";
import * as yup from "yup";
import { TextFormField } from '../../shared/components/FormFields/TextFormField'
import CitiesData from './../../Data/Citiescopy.json';  // Assuming JSON structured as { "StateName": ["City1", "City2"] }
import StatesData from './../../Data/States.json';
import useContactStore from '../../shared/store/contactStore'
import { AutoCompleteField } from '../../shared/components/FormFields/AutoCompleteField';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import api from '../../assets/api/usePostApi';
import ContactPersonDetail from './ContactPersonDetail';
import { latitudeValidation, longitudeValidation } from './SchemaLatLong';


const Apartment = () => {
    const { contactPersons, resetContactPersons } = useContactStore();
    const [cityOptions, setCityOptions] = useState([]);
    const StateOption = useMemo(() => {
        return StatesData.map((location) => ({
            id: location.id,
            label: location.name,
            value: location.name,
        })) || [];
    }, [StatesData]);

    const designationOption = [
        { label: "Apartment Owner", value: "Apartment Owner" },
        { label: "Tenant/Renter", value: "Tenant/Renter" },
        { label: "Association President", value: "Association President" },
        { label: "Secretary", value: "Secretary" },
        { label: "Treasurer", value: "Treasurer" },
        { label: "Committee Members", value: "Committee Members" },
        { label: "Property Manager", value: "Property Manager" }
    ];

    const validationSchema = yup.object().shape({
        apartment_name: yup.string().required("Apartment Name is required"),
        no_of_flats: yup.number().required("Number of Flats is required"),
        address: yup.string().required("Address is required"),
        city_id: yup.string().required("City is required"),
        state_id: yup.string().required("State is required"),
        country_id: yup.string().required("Country is required"),
        pincode: yup.string().required("Pincode is required"),
        latitude: latitudeValidation,
        longitude: longitudeValidation

    });
    const initialValues = {
        apartment_name: "",
        no_of_flats: "",
        address: "",
        street: "",
        landmark: "",
        city_id: "",
        state_id: "",
        country_id: "India",
        pincode: "",
        latitude: "",
        longitude: "",
    };

    const mutation = useMutation({
        mutationFn: (formData) => api.post("collab-apartment-on-boarding/create", formData), // No need to repeat the full URL
    });

    const handleSubmit = (values, { setSubmitting, resetForm }) => {
        if (!contactPersons || contactPersons.length === 0) {
            return toast.warning("Add at least one contact person", {
                position: "top-right",
                autoClose: 3000,
                theme: "light",
            });
        }

        const ojb = {
            ...values,
            contact_person_details: contactPersons.map(({ id, ...rest }) => rest)
        };
        mutation.mutate(ojb, {
            onSuccess: (response) => {
                if (response?.data) {
                    toast.success(` ${response.data.message}`, {
                        position: "top-right",
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        theme: "light",
                    });
                } else {
                    toast.warning("Message sent, but no response from server.", {
                        position: "top-right",
                        autoClose: 3000,
                        theme: "light",
                    });
                }

                resetForm();
                resetContactPersons()
            },
            onError: (error) => {
                console.error("Error Response:", error.response);
                toast.error(`Error sending message: ${error.response?.data?.message || error.message}`, {
                    position: "top-right",
                    autoClose: 5000,
                    theme: "dark",
                });
            },
            onSettled: () => {
                setSubmitting(false);
            }
        });
    };
    return (
        <>
            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                {({ setFieldValue }) => (
                    <Form>

                        <Grid2 container spacing={5}>
                            <Grid2 children size={{ xs: 12, sm: 6 }}>
                                <Field
                                    name="apartment_name"
                                    component={TextFormField}
                                    label="Apartment Name"
                                    isRequired={true}
                                />
                            </Grid2>
                            <Grid2 children size={{ xs: 12, sm: 6 }}>
                                <Field
                                    name="no_of_flats"
                                    component={TextFormField}
                                    label="Number Of Flats"
                                    isRequired={true}
                                    type={"number"}
                                />
                            </Grid2>
                            <Grid2 children size={12}>
                                <Field
                                    name="address"
                                    component={TextFormField}
                                    label="Address"
                                    isRequired={true}
                                />
                            </Grid2>
                            <Grid2 children size={{ xs: 12, sm: 6 }}>
                                <Field
                                    name="street"
                                    component={TextFormField}
                                    label="Street"
                                    isRequired={true}
                                />
                            </Grid2>
                            <Grid2 children size={{ xs: 12, sm: 6 }}>
                                <Field
                                    name="landmark"
                                    component={TextFormField}
                                    label="Landmark"
                                    isRequired={true}
                                />
                            </Grid2>
                            <Grid2 children size={{ xs: 12, sm: 6 }}>
                                <Field
                                    name="country_id"
                                    component={AutoCompleteField}
                                    options={[{
                                        id: 101,
                                        label: "India",
                                        value: "India",
                                    }]}
                                    label="Country"
                                    isRequired
                                />
                            </Grid2>

                            <Grid2 children size={{ xs: 12, sm: 6 }}>
                                <Field
                                    name="state_id"
                                    component={AutoCompleteField}
                                    options={StateOption}
                                    label="State"
                                    isRequired
                                    onValueChange={(newValue) => {
                                        const selectedStateId = newValue?.id || "";
                                        setFieldValue("state_id", selectedStateId);
                                        setFieldValue("city_id", "");
                                        const stateData = CitiesData.find(item => item.id === selectedStateId);
                                        const cities = stateData ? stateData.cities : [];
                                        setCityOptions(
                                            cities.map(location => ({
                                                id: location.id,
                                                label: location.name,
                                                value: location.name
                                            }))
                                        );
                                    }}
                                />
                            </Grid2>
                            <Grid2 children size={{ xs: 12, sm: 6 }}>
                                <Field
                                    name="city_id"
                                    component={AutoCompleteField}
                                    options={cityOptions}
                                    label="City"
                                    isRequired
                                    onChange={(event, newValue) => {
                                        setFieldValue("city_id", newValue?.id || "");
                                    }}
                                />
                            </Grid2>
                            <Grid2 children size={{ xs: 12, sm: 6 }}>
                                <Field
                                    name="pincode"
                                    component={TextFormField}
                                    label="Pincode"
                                    isRequired={true}
                                    type={"number"}
                                />
                            </Grid2>
                            <Grid2 children size={{ xs: 12, sm: 6 }}>
                                <Field
                                    name="latitude"
                                    component={TextFormField}
                                    label="Latitude"
                                    isRequired={true}
                                    type={"number"}
                                />
                            </Grid2>
                            <Grid2 children size={{ xs: 12, sm: 6 }}>
                                <Field
                                    name="longitude"
                                    component={TextFormField}
                                    label="Longitude"
                                    isRequired={true}
                                    type={"number"}
                                />
                            </Grid2>

                        </Grid2>
                        <CustomDivider />
                        <ContactPersonDetail designationOption={designationOption} />
                        <Grid2 container my={5}>
                            <Grid2 children size={{ xs: 12, sm: 7 }} />
                            <Grid2 children size={{ xs: 12, sm: 5 }}>
                                <Button variant='contained' fullWidth type='submit'>Submit</Button>
                            </Grid2>
                        </Grid2>
                    </Form>
                )}
            </Formik>

        </>
    )
}

export default Apartment